import {
  Box,
  Button,
  FormControlLabel,
  FormGroup,
  Switch,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";
import DownloadIcon from "@mui/icons-material/DownloadOutlined";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import EditCertificate from "./edit-certificate/edit-certificate";
import {
  downloadTemporaryFileRequest,
  updateCertificationVerifiedRequest,
} from "../../../../../utils/APICalls";
import { getUserCertificationsRequest } from "../../../../../redux/users/slice";
import Loading from "../../../../../components/loading/loading";
import ErrorNotification from "../../../../../components/error-notification/error-notification";

type Props = {
  certificationType: string;
  certificate: any;
  inspectorFirmId: any;
  certificationMethods: any;
  isMyOrganisation?: boolean;
  isTenantUser?: boolean;
};

const FannieMaeCertificateDetailRow = ({
  certificationType,
  certificate,
  inspectorFirmId,
  certificationMethods,
  isMyOrganisation,
  isTenantUser,
}: Props) => {
  const dispatch = useDispatch();
  const [showEditCertification, setShowEditCertification] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isVerified, setIsVerified] = useState(
    certificate?.status === "Approved"
  );

  useEffect(() => {
    setIsVerified(certificate?.status === "Approved");
  }, [certificate]);

  const handleVerifiedChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isChecked = event.target.checked;
    setIsVerified(isChecked);
    setShowLoader(true);
    setErrorMessage("");
    const response = await updateCertificationVerifiedRequest(
      inspectorFirmId,
      isChecked,
      "FannieMae"
    );
    setShowLoader(false);
    if (response?.status === 200 || response?.status === 204) {
      dispatch(getUserCertificationsRequest({ id: inspectorFirmId }));
    } else {
      setShowErrorNotification(true);
      setErrorMessage(
        response?.data?.detail || "Failed to update verification status."
      );
      setIsVerified(!isChecked);
    }
  };

  const onDownloadClick = async (key: string, filename: string) => {
    if (!key) {
      return;
    }
    setShowLoader(true);
    try {
      const response = await downloadTemporaryFileRequest(key);
      if (response && response.status === 200) {
        let fileUrl = response.data;

        if (typeof fileUrl === "string") {
          fileUrl = fileUrl.trim();
          if (fileUrl.startsWith('"') && fileUrl.endsWith('"')) {
            fileUrl = JSON.parse(fileUrl);
          }
        }

        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = filename;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else if (response && response.status >= 400) {
        setShowErrorNotification(true);
        setErrorMessage("Failed to download document. Please try again.");
      }
    } catch (error) {
      setShowErrorNotification(true);
      setErrorMessage("Failed to download document. Please try again.");
    } finally {
      setShowLoader(false);
    }
  };

  const renderStatus = (status: string) => {
    const statusConfig: Record<
      string,
      { icon: JSX.Element; text: string; color: string }
    > = {
      Verified: {
        icon: <CheckCircleOutlineIcon color="success" />,
        text: "Verified",
        color: "success",
      },
      Pending: {
        icon: <PendingOutlinedIcon color="warning" />,
        text: "Pending Verification",
        color: "warning",
      },
      Incomplete: {
        icon: <WarningAmberIcon color="error" />,
        text: "Incomplete",
        color: "error",
      },
    };

    const normalizedStatus = status === "Approved" ? "Verified" : status;
    const config =
      statusConfig[normalizedStatus] || statusConfig["Incomplete"];

    return (
      <>
        {config.icon}
        <Typography color={config.color} sx={{ fontStyle: "italic" }}>
          {config.text}
        </Typography>
      </>
    );
  };

  return (
    <>
      <Box className="user-name-layout">
        <Typography className="label-txt">{certificationType}</Typography>
        {certificate ? (
          <Box
            className="certification-columns-scroll"
            sx={{
              display: "flex",
              overflowX: "auto",
              flex: 1,
              minWidth: 0,
            }}
          >
            <Box sx={{ width: "226px", flexShrink: 0, mr: 3 }}>
              <Typography className="label-header-text">
                Certification Method
              </Typography>
              <Typography sx={{ overflowWrap: "break-word" }}>
                {certificationMethods
                  .filter(
                    (method: { certificationMethod: any }) =>
                      method.certificationMethod ===
                      certificate?.certificationMethod
                  )
                  .map((method: { displayName: any }) => method.displayName)}
              </Typography>
            </Box>
            <Box sx={{ width: "226px", flexShrink: 0, mr: 3 }}>
              <Typography className="label-header-text">Certificate</Typography>
              {certificate?.certificateFileName ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CheckCircleOutlineIcon color="success" />
                  <DownloadIcon
                    color="warning"
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      onDownloadClick(
                        certificate?.certificateKey,
                        certificate?.certificateFileName
                      )
                    }
                  />
                </Box>
              ) : certificate?.certificationMethod === "HUDREAC" &&
                certificate?.certificationMetadata?.certificationNumber ? (
                <CheckCircleOutlineIcon color="success" />
              ) : (
                <Typography>n/a</Typography>
              )}
            </Box>
            <Box sx={{ width: "110px", flexShrink: 0, mr: 2 }}>
              <Typography
                className="label-header-text"
                sx={{ width: "110px !important" }}
              >
                Declaration Doc
              </Typography>
              <Typography>
                {certificate?.declarationFileName ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleOutlineIcon color="success" />
                    <DownloadIcon
                      color="warning"
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        onDownloadClick(
                          certificate?.declarationKey,
                          certificate?.declarationFileName
                        )
                      }
                    />
                  </Box>
                ) : (
                  <Typography color="primary">
                    <Button
                      variant="text"
                      startIcon={<AddOutlinedIcon />}
                      onClick={() => {
                        setShowEditCertification(true);
                      }}
                    >
                      ADD
                    </Button>
                  </Typography>
                )}
              </Typography>
            </Box>
            <Box sx={{ width: "140px", flexShrink: 0, mr: 1 }}>
              <Typography
                className="label-header-text"
                sx={{ width: "140px !important" }}
              >
                Status
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {renderStatus(certificate?.status || "Incomplete")}
              </Box>
            </Box>
            {isTenantUser && (
              <Box sx={{ width: "100px", flexShrink: 0, mr: 1 }}>
                <Typography
                  className="label-header-text"
                  sx={{ width: "100px !important" }}
                >
                  Verified?
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isVerified}
                        onChange={handleVerifiedChange}
                        disabled={showLoader}
                      />
                    }
                    label={isVerified ? "Yes" : "No"}
                  />
                </FormGroup>
              </Box>
            )}
            <Box sx={{ display: "flex", alignItems: "flex-start", pt: "28px" }}>
              <EditOutlinedIcon
                sx={{ cursor: "pointer" }}
                color="primary"
                onClick={() => {
                  setShowEditCertification(true);
                }}
              />
            </Box>
          </Box>
        ) : (
          [
            !isMyOrganisation && (
              <Button
                variant="outlined"
                startIcon={<AddOutlinedIcon />}
                onClick={() => {
                  setShowEditCertification(true);
                }}
              >
                Add Certification
              </Button>
            ),
          ]
        )}
        {showEditCertification && (
          <EditCertificate
            showDialog={showEditCertification}
            closeDialog={() => {
              setShowEditCertification(false);
            }}
            data={{ ...certificate, certificationType, id: inspectorFirmId }}
          />
        )}
      </Box>
      {showLoader && <Loading />}
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
    </>
  );
};

export default FannieMaeCertificateDetailRow;
